import {
  createContext,
  useContext,
  useMemo,
  type CSSProperties,
  type ReactNode,
} from 'react'
import { VideoTrack } from '@livekit/components-react'
import { Track } from 'livekit-client'
import { useEventLiveKitTrackRefs } from '@/components/livekit/EventLiveKitProvider'
import type { CameraSelection, DestinationCamera } from '../types'

interface CameraContextValue {
  cameras: DestinationCamera[]
  selection: CameraSelection
  viewerCount: number | null
}

const CameraContext = createContext<CameraContextValue | null>(null)

export function CameraProvider({
  cameras,
  selection,
  viewerCount,
  children,
}: CameraContextValue & { children: ReactNode }) {
  const value = useMemo(
    () => ({ cameras, selection, viewerCount }),
    [cameras, selection, viewerCount],
  )

  return <CameraContext.Provider value={value}>{children}</CameraContext.Provider>
}

function useCameraContext(): CameraContextValue {
  const context = useContext(CameraContext)
  if (!context) throw new Error('Camera hooks must be used inside <CameraProvider>.')
  return context
}

export function useCameras(): DestinationCamera[] {
  return useCameraContext().cameras
}

export function useCamera(identity: string): DestinationCamera | null {
  return useCameras().find((camera) => camera.identity === identity) ?? null
}

export function useCameraSelection(): CameraSelection {
  return useCameraContext().selection
}

export function useViewerCount(): number | null {
  return useCameraContext().viewerCount
}

export interface CameraVideoProps {
  identity: string
  fit?: 'cover' | 'contain'
  muted?: boolean
  className?: string
  previewClassName?: string
  alt?: string
  fallback?: ReactNode
}

/**
 * The only destination surface that understands LiveKit track references.
 * Components select a semantic camera identity and never touch the room,
 * subscriptions, or transport SDK.
 */
export function CameraVideo({
  identity,
  fit = 'cover',
  muted = true,
  className,
  previewClassName,
  alt,
  fallback = null,
}: CameraVideoProps) {
  const camera = useCamera(identity)
  const trackRefs = useEventLiveKitTrackRefs()
  const trackRef = useMemo(
    () =>
      trackRefs.find(
        (ref) =>
          ref.participant.identity === identity &&
          ref.source === Track.Source.Camera,
      ) ?? null,
    [identity, trackRefs],
  )

  if (!trackRef) {
    if (camera?.previewImageUrl) {
      return (
        <img
          className={previewClassName}
          src={camera.previewImageUrl}
          alt={alt ?? camera.name}
          style={{ objectFit: fit } satisfies CSSProperties}
        />
      )
    }
    return <>{fallback}</>
  }

  return (
    <VideoTrack
      trackRef={trackRef}
      playsInline
      muted={muted}
      controls={false}
      disablePictureInPicture
      disableRemotePlayback
      className={className}
      style={{ objectFit: fit }}
    />
  )
}
